import {
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  verifyPasswordResetCode,
} from "firebase/auth";
import app from "../firebaseConfig";
import { isDemoMode } from "../demo/demoStore";

const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

onAuthStateChanged(auth, async (user) => {
  if (user && user.emailVerified) {
    try {
      const token = await user.getIdToken(true);
      localStorage.setItem("token", token);
    } catch (error) {
      console.error("Error refreshing token:", error);
    }
  }
});

export const getValidToken = async () => {
  // Safety net: every service's axios interceptor calls this before any real
  // request goes out. If a service function forgot its isDemoMode() mock guard,
  // this throws here instead of silently firing an unauthenticated request at
  // production. Catches missing demo support generically, with no per-file edits.
  if (isDemoMode()) {
    throw new Error(
      "Blocked a real API call while in demo mode — this feature is missing its demo mock guard.",
    );
  }
  const user = auth.currentUser;
  if (user && user.emailVerified) {
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error("Error getting fresh token:", error);
      return await refreshToken();
    }
  }
  return null;
};

export const refreshToken = async () => {
  const user = auth.currentUser;
  if (user && user.emailVerified) {
    try {
      const token = await user.getIdToken(true);
      localStorage.setItem("token", token);
      return token;
    } catch (error) {
      console.error("Error refreshing token:", error);
      return null;
    }
  }
  return null;
};

export const login = async (credentials) => {
  const { email, password } = credentials;
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const user = userCredential.user;

    // Check if email is verified
    if (!user.emailVerified) {
      throw new Error("EMAIL_NOT_VERIFIED");
    }

    const isGoogleUser = user.providerData.some(
      (provider) => provider.providerId === "google.com",
    );
    if (user.emailVerified || isGoogleUser) {
      const token = await user.getIdToken();
      localStorage.setItem("token", token);
    }
    return user;
  } catch (error) {
    throw error;
  }
};

export const loginWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const token = await result.user.getIdToken();
    localStorage.setItem("token", token);
    return result.user;
  } catch (error) {
    throw error;
  }
};

export const registerUser = async (userData) => {
  const { email, password } = userData;
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const user = userCredential.user;
    //const token = await user.getIdToken();
    //localStorage.setItem('token', token);
    await sendEmailVerification(user);

    console.log("User registered successfully. Email verification required.");

    return user;
  } catch (error) {
    throw error;
  }
};

export const logout = async () => {
  try {
    await signOut(auth);
    localStorage.removeItem("token");
    localStorage.removeItem("selectedContext");
    localStorage.removeItem("selectedGroupId");
    localStorage.removeItem("selectedGroupName");
    localStorage.removeItem("selectedCategory");
    localStorage.removeItem("selectedMonths");
  } catch (error) {
    throw error;
  }
};

export const getUserId = async () => {
  if (isDemoMode()) return Promise.resolve('demo-user-001');
  return new Promise((resolve, reject) => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        resolve(user.uid);
      } else {
        reject("No user is logged in");
      }
    });
  });
};

export const sendVerificationEmail = async () => {
  const user = auth.currentUser;
  if (user && !user.emailVerified) {
    try {
      await sendEmailVerification(user);
      return true;
    } catch (error) {
      throw error;
    }
  }
  return false;
};

export const checkEmailVerified = () => {
  const user = auth.currentUser;
  if (!user) return false;

  const isGoogleUser = user.providerData.some(
    (provider) => provider.providerId === "google.com",
  );
  return user.emailVerified || isGoogleUser;
};

export const reloadUser = async () => {
  const user = auth.currentUser;
  if (user) {
    await user.reload();
    return user.emailVerified;
  }
  return false;
};

export const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error) {
    throw error;
  }
};

export const verifyPasswordResetCodeFunction = async (code) => {
  try {
    const email = await verifyPasswordResetCode(auth, code);
    return email;
  } catch (error) {
    throw error;
  }
};

export const confirmPasswordResetFunction = async (code, newPassword) => {
  try {
    await confirmPasswordReset(auth, code, newPassword);
    return true;
  } catch (error) {
    throw error;
  }
};

export const handleEmailVerification = async (actionCode) => {
  try {
    await applyActionCode(auth, actionCode);
    return true;
  } catch (error) {
    throw error;
  }
};
